import { i18n } from '@lingui/core'

/**
 * A 2-decimal money string, optionally negative — the only shape `formatPoints`
 * renders, and the shape callers must validate against before trusting a figure.
 */
export const MONEY_PATTERN = /^-?\d+\.\d{2}$/

/**
 * Renders a 2-decimal money string for display, grouped for the active locale.
 *
 * Deliberately NOT `formatNumber`: that one routes every value under 0.01 —
 * negatives included — into a `<0.01` token, and can render compactly. A balance
 * must show its real figure: `0.00` is `0.00`, `-12.34` keeps its minus, and
 * `1200.00` is never `1.2K`.
 *
 * The value never becomes a float. The integer part is grouped as a `bigint`, so
 * a figure beyond `Number.MAX_SAFE_INTEGER` keeps every digit, and the two
 * fraction digits are re-joined verbatim.
 *
 * Total by construction: a string that is not 2-decimal money comes back
 * unchanged. This util is on the render path and the app has no ErrorBoundary,
 * so a throw here would blank the dashboard; the fail-fast gate is
 * `deriveBuzzCardState`, one layer up, where a bad value has a state to become.
 */
export function formatPoints(value: string): string {
  if (typeof value !== 'string' || !MONEY_PATTERN.test(value)) {
    return value
  }

  const isNegative = value.startsWith('-')
  const [integerPart, fractionPart] = (isNegative ? value.slice(1) : value).split('.')

  const parts = new Intl.NumberFormat(i18n.locale || undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).formatToParts(BigInt(integerPart))

  const formatted = parts.map((part) => (part.type === 'fraction' ? fractionPart : part.value)).join('')

  return isNegative ? `-${formatted}` : formatted
}
